import type { KeyboardEventHandler } from 'react';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { FormatOptionLabelMeta } from 'react-select';
import SelectAsync from 'react-select';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import type { HcpSearchResult, KolSearchForm, KolSearchResult  } from '@trinity/chat';
import { KolSearchStatusEnum } from '@trinity/chat';
import { buildSelectStyles, buildSelectTheme } from '@trinity/chat-components/components/Chat.Filters/Presentation.js';
import { SearchQueryHint } from '@trinity/chat-components/components/presentation/Message.QueryHint';
import { SelectLocation } from '@trinity/chat-components/components/Search/Location';
import { useTheme } from '@trinity/chat-components/components/Theme';
import { useHasSmartRouting } from '@trinity/chat-components/containers/Chat/selectors';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { useBrowserLocation } from '@trinity/chat-components/hooks/useBrowserLocation';
import { BrowserLocationError } from './BrowserLocationError';
import {
  DispatchKolSearchFormContext,
  KolSearchStatusContext,
  KolSearchSubmitContext,
  KolSearchFormContext,
} from './context';
import { BasicSystemUserMessage } from './Message.System.Basic';

export const KolSearchFormMessage = () => {
  const searchStatus = useContext(KolSearchStatusContext);
  const handleSubmit = useContext(KolSearchSubmitContext);
  const formState = useContext(KolSearchFormContext);
  const setFormState = useContext(DispatchKolSearchFormContext);
  const inputFocused = useRef(false);

  const copy = `Let's get started. First lets find the HCP you'd like to profile!`;
  const instructions = `Enter details to find the HCP you would like to profile.`;

  const submitDisabled = useMemo(() => {
    return (!formState.name && !formState.location) || searchStatus !== KolSearchStatusEnum.FormEntry;
  }, [formState.location, formState.name, searchStatus]);

  const inputDisabled = useMemo(() => {
    return searchStatus !== KolSearchStatusEnum.FormEntry;
  }, [searchStatus]);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(e => {

    if (!inputFocused.current) return;

    if (submitDisabled) return;

    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  }, [submitDisabled, handleSubmit]);

  const handleInputBlur = useCallback(() => {
    inputFocused.current = false;
  }, []);

  const handleInputFocus = useCallback(() => {
    inputFocused.current = true;
  }, []);

  const onKolSelect = useCallback((kol: KolSearchResult) => {
    setFormState({ kolId: kol.id, name: kol.name });
    handleSubmit();
  }, [handleSubmit, setFormState]);

  return (
    <MessageContainer>
      <BasicSystemUserMessage>
        <Body>
          {copy}
          <Divider />
          <Instructions>{instructions}</Instructions>

          <KolSearchInputForm
            formState={formState}
            inputDisabled={inputDisabled}
            onKeyDown={handleKeyDown}
            onInputFocus={handleInputFocus}
            onInputBlur={handleInputBlur}
            onKolSelect={onKolSelect}
            setFormState={setFormState} />
          <Divider />
          <SearchQueryHint
            disabled={submitDisabled}
            onClick={handleSubmit}
            value="Search for profiles." />
        </Body>
      </BasicSystemUserMessage>
    </MessageContainer>
  );
};

// Base type for search results
type BaseSearchResult = {
  name?: string;
} & ({ id?: number } | { hcpIdentifier?: string });

type HcpSelectProps<T extends BaseSearchResult = KolSearchResult> = {
  disabled?: boolean;
  formatOptionLabel?: (data: T, formatOptionLabelMeta: FormatOptionLabelMeta<T>) => React.ReactNode;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  onInputFocus: () => void;
  onInputBlur: () => void;
  onInputChange: (e: string) => void;
  onSelection: (result: T) => void;
  placeholder?: string;
  inputValue: string;
  isClearable?: boolean;
  value?: T | null;
  queryFn?: (params: {
    name: string;
    brandIdentifier: string;
    searchIndex?: string;
    connectionIdentifier?: string;
  }) => Promise<{ results: T[] }>;
  searchIndex?: string;
  connectionIdentifier?: string;
  getOptionLabel?: (option: T) => string;
  getOptionValue?: (option: T) => string;
};

export function SelectHcp<T extends BaseSearchResult = KolSearchResult>({
  disabled,
  formatOptionLabel,
  onKeyDown,
  onInputFocus,
  onInputBlur,
  onInputChange,
  onSelection,
  placeholder = 'Search HCPs',
  inputValue,
  isClearable,
  value,
  queryFn,
  searchIndex,
  connectionIdentifier,
  getOptionLabel,
  getOptionValue,
}: HcpSelectProps<T>) {

  const brandIdentifier = useActiveBrandIdentifier();

  const apiClient = useChatApiClient();

  const searchQuery = useQuery({
    queryKey: [
      'get:search',
      inputValue,
      brandIdentifier,
      queryFn?.name || 'default',
      searchIndex,
      connectionIdentifier,
    ],
    queryFn: () => queryFn
      ? queryFn({ name: inputValue, brandIdentifier, searchIndex, connectionIdentifier })
      : (apiClient.search.searchKols({ name: inputValue, brandIdentifier }) as unknown as Promise<{ results: T[] }>),
    enabled: inputValue?.length > 2 && !disabled,
    placeholderData: { results: [] },
  });

  const theme = useTheme();
  const selectStyles = useMemo(() => buildSelectStyles(theme), [theme]);
  const selectTheme = useMemo(() => buildSelectTheme(theme), [theme]);

  const defaultGetOptionLabel = (option: T): string => option.name;

  const defaultGetOptionValue = (option: T): string => {
    if ('id' in option) return option.id.toString();
    if ('hcpIdentifier' in option) return option.hcpIdentifier;
    return '';
  };

  const defaultFormatOptionLabel = (data: T) => {
    if (formatOptionLabel) return formatOptionLabel(data, {} as FormatOptionLabelMeta<T>);

    if ('specialties' in data && 'affiliations' in data) {
      return formatKolOptionLabel(data as unknown as KolSearchResult);
    }

    if ('specialty' in data && 'primaryAffiliationName' in data) {
      return formatHcpOptionLabel(data as unknown as HcpSearchResult);
    }

    // Fallback to just name
    return <div>{data.name}</div>;
  };

  return (
    <SelectAsync<T>
      menuPortalTarget={document.body}
      isDisabled={disabled}
      isClearable={isClearable}
      onKeyDown={searchQuery.data.results.length ? undefined : onKeyDown}
      onFocus={onInputFocus}
      onBlur={onInputBlur}
      onChange={onSelection}
      inputValue={inputValue}
      value={value}
      isLoading={searchQuery.isFetching}
      options={searchQuery.data.results}
      getOptionLabel={getOptionLabel || defaultGetOptionLabel}
      getOptionValue={getOptionValue || defaultGetOptionValue}
      formatOptionLabel={defaultFormatOptionLabel}
      noOptionsMessage={() => null}
      onInputChange={(e, meta) => meta.action === 'input-change' ? onInputChange(e) : null}
      placeholder={placeholder}
      styles={selectStyles}
      filterOption={() => true}
      theme={selectTheme} />
  );
}

type FormProps = {
  className?: string;
  inputDisabled: boolean;
  onInputFocus: () => void;
  onInputBlur: () => void;
  onKeyDown: KeyboardEventHandler<HTMLInputElement>;
  onKolSelect: (kol: KolSearchResult) => void;
  formState: Partial<KolSearchForm>;
  setFormState: (form: Partial<KolSearchForm>) => void;
};

export const KolSearchInputForm = ({
  className,
  formState,
  inputDisabled,
  onInputBlur,
  onInputFocus,
  onKeyDown,
  onKolSelect,
  setFormState,
}: FormProps) => {

  const brandIdentifier = useActiveBrandIdentifier();

  const apiClient = useChatApiClient();

  const searchQuery = useQuery({
    queryKey: ['get:kol-search', formState.name, brandIdentifier],
    queryFn: () => apiClient.search.searchKols({ name: formState.name, brandIdentifier }),
    enabled: formState.name?.length > 2,
    placeholderData: { results: [] },
  });

  const [locationError, setLocationError] = useState<string | null>(null);
  const { getLocation, isSupported, state } = useBrowserLocation({
    onSuccess: result => {
      setFormState({ location: { id: 'browser-location', latitude: result.latitude, longitude: result.longitude, name: null } });
    }, onError: setLocationError,
  });

  const hasSmartRouting = useHasSmartRouting();

  return (
    <div className={className}>
      <FormItem>
        <Label>Search by Name</Label>
        <SelectHcp
          placeholder="Name"
          disabled={inputDisabled}
          onKeyDown={searchQuery.data.results.length ? undefined : onKeyDown}
          onInputFocus={onInputFocus}
          onInputBlur={onInputBlur}
          onInputChange={name => setFormState({ ...formState, name })}
          onSelection={kol => onKolSelect(kol)}
          inputValue={formState.name} />
      </FormItem>
      {hasSmartRouting && (
        <>
          <Label>OR</Label>
          <FormItem>
            <Label>Search by address or current location</Label>
            <SelectLocation
              styles={{
                container: (baseStyles) => ({ ...baseStyles, width: '100%' }),
              }}
              isDisabled={inputDisabled}
              value={formState.location}
              onChange={location => setFormState({ ...formState, location })} />
            {isSupported &&
              <GetLocationSection>
                <GetLocationButton onClick={getLocation}>Share my current location</GetLocationButton>
                {state === 'error' && <BrowserLocationError errorMessage={locationError} />}
              </GetLocationSection>}
          </FormItem>
        </>
      )}
    </div >
  );
};

function formatKolOptionLabel(result: KolSearchResult) {
  const specialty = result.specialties?.[0];
  const primaryAffiliation = result.affiliations?.[0]?.name;
  return (
    <div>
      {result.name}{specialty ? <KolOptionSubText>({specialty})</KolOptionSubText> : null}
      {primaryAffiliation ? <KolResultSecondaryText>{primaryAffiliation}</KolResultSecondaryText> : null}
    </div>
  );
}

function formatHcpOptionLabel(result: HcpSearchResult) {
  const specialty = result.specialty?.trim();
  const affiliation = result.primaryAffiliationName && result.primaryAffiliationName !== 'N'
    ? result.primaryAffiliationName.trim()
    : null;
  return (
    <div>
      {result.name}{specialty ? <KolOptionSubText>({specialty})</KolOptionSubText> : null}
      {affiliation ? <KolResultSecondaryText>{affiliation}</KolResultSecondaryText> : null}
    </div>
  );
}

const KolResultSecondaryText = styled.div(({ theme }) => ({
  color: theme.palette.gray.main,
  marginTop: 5,
  fontSize: 15,
}));
const KolOptionSubText = styled.span(({ theme }) => ({
  fontSize: 14,
  marginLeft: 5,
  color: theme.palette.gray.main,
}));


const Body = styled.div({
  width: 500,
});

const MessageContainer = styled.div({
  paddingRight: 20,
  paddingLeft: 20,
});

const Divider = styled.div(({ theme }) => ({
  width: '100%',
  height: 2,
  backgroundColor: theme.palette.gray.light2,
  margin: '15px 0',
}));

const Instructions = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  marginBottom: 20,
}));

const Label = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  marginBottom: 10,
}));

const FormItem = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  marginBottom: 10,
});

const GetLocationSection = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  marginTop: 5,
});

const GetLocationButton = styled.div(({ theme }) => ({
  cursor: 'pointer',
  color: theme.palette.sentiment.primary.main,
  textDecoration: 'underline',
  fontSize: 14,
}));